import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { 
  SupplyChain, 
  ResilienceScore, 
  SustainabilityMetrics, 
  Disruption, 
  Recommendation, 
  Simulation, 
  DisruptionScenario, 
  Task, 
  Analytics,
  SubscriptionPlan,
  CurrentSubscription,
  FeatureFlags,
  UsageStatistics,
  FeatureUsage
} from '../types';

// Define the API base URL
const baseUrl = process.env.REACT_APP_API_URL || '';

// Custom base query that handles 404s gracefully
const baseQueryWithErrorHandling = fetchBaseQuery({ 
  baseUrl,
  prepareHeaders: (headers) => {
    // Get token from localStorage or another storage method
    const token = localStorage.getItem('access_token');
    if (token) {
      headers.set('Authorization', `Bearer ${token}`);
    }
    return headers;
  },
});

// Wrapper that silences 404 errors
const customBaseQuery: typeof baseQueryWithErrorHandling = async (args, api, extraOptions) => {
  const result = await baseQueryWithErrorHandling(args, api, extraOptions);
  
  // Silently handle 404 errors (endpoints not implemented yet)
  if (result.error?.status === 404) {
    return { data: null } as any;
  }
  
  return result;
};

// Create the API service using RTK Query
export const api = createApi({
  reducerPath: 'api',
  baseQuery: customBaseQuery,
  tagTypes: ['SupplyChain', 'Simulations', 'Disruptions', 'Tasks', 'Analytics', 'Auth', 'Subscription', 'Usage', 'FeatureFlags'],
  endpoints: (builder) => ({
    // Authentication endpoints
    login: builder.mutation<{ access_token: string; token_type: string; expires_at: number; user_id: string; organization_id: string; role: string }, { email: string; password: string }>({
      query: (credentials) => {
        // OAuth2 uses form-urlencoded with username/password fields
        const formData = new URLSearchParams();
        formData.append('username', credentials.email);
        formData.append('password', credentials.password);
        return {
          url: '/api/v1/auth/login',
          method: 'POST',
          body: formData.toString(),
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        };
      },
      invalidatesTags: ['Auth'],
    }),
    register: builder.mutation<any, { email: string; password: string; name: string; company: string; job_title: string }>({
      query: (userData) => ({
        url: '/api/v1/auth/register',
        method: 'POST',
        body: {
          email: userData.email,
          password: userData.password,
          full_name: userData.name,
          organization_name: userData.company || 'Default Organization',
        },
      }),
    }),

    // Agent endpoints
    processTask: builder.mutation<any, any>({
      query: (task) => ({
        url: '/api/v1/agents/process-task',
        method: 'POST',
        body: task,
      }),
      invalidatesTags: ['Tasks'],
    }),
    getTaskHistory: builder.query<any[], { limit?: number }>({
      query: (params) => ({
        url: '/api/v1/agents/history',
        params,
      }),
      providesTags: ['Tasks'],
    }),
    clearTaskHistory: builder.mutation<{ status: string; message: string }, void>({
      query: () => ({
        url: '/api/v1/agents/history',
        method: 'DELETE',
      }),
      invalidatesTags: ['Tasks'],
    }),
    getAgentCapabilities: builder.query<any, void>({
      query: () => '/api/v1/agents/capabilities',
    }),
    getAgentStatus: builder.query<any, void>({
      query: () => '/api/v1/agents/status',
      providesTags: ['Tasks'],
    }),
    getTaskQueue: builder.query<any, { limit?: number }>({
      query: (params) => ({
        url: '/api/v1/agents/task-queue',
        params,
      }),
      providesTags: ['Tasks'],
    }),
    searchMemory: builder.query<any, { query: string; limit?: number }>({
      query: (params) => ({
        url: '/api/v1/memory/search',
        params,
      }),
    }),
    getMemoryStats: builder.query<any, void>({
      query: () => '/api/v1/memory/stats',
    }),

    // Subscription endpoints
    getSubscriptionPlans: builder.query<SubscriptionPlan[], void>({
      query: () => '/api/v1/subscription/plans',
      providesTags: ['Subscription'],
    }),
    getCurrentSubscription: builder.query<CurrentSubscription, void>({
      query: () => '/api/v1/subscription/current',
      providesTags: ['Subscription'],
    }),
    updateSubscription: builder.mutation<{ status: string; message: string; effective_date: string }, { plan_id: string; billing_cycle: string; payment_method_id: string }>({
      query: (updateData) => ({
        url: '/api/v1/subscription/update',
        method: 'PUT',
        body: updateData,
      }),
      invalidatesTags: ['Subscription'],
    }),

    // Usage Analytics endpoints
    getUsageStatistics: builder.query<UsageStatistics, { period: string }>({
      query: (params) => ({
        url: '/api/v1/usage/statistics',
        params,
      }),
      providesTags: ['Usage'],
    }),
    getFeatureUsage: builder.query<FeatureUsage, void>({
      query: () => '/api/v1/usage/features',
      providesTags: ['Usage'],
    }),

    // Feature Flag endpoints
    getFeatureFlags: builder.query<FeatureFlags, void>({
      query: () => '/api/v1/feature-flags',
      providesTags: ['FeatureFlags'],
    }),
    updateFeatureFlag: builder.mutation<{ name: string; enabled: boolean; updated_at: string }, { flag_name: string; enabled: boolean }>({
      query: ({ flag_name, enabled }) => ({
        url: `/api/v1/feature-flags/${flag_name}`,
        method: 'PATCH',
        body: { enabled },
      }),
      invalidatesTags: ['FeatureFlags'],
    }),

    // Simulation endpoints
    createSimulation: builder.mutation<{ simulation_id: string; status: string; created_at: string }, any>({
      query: (simulation) => ({
        url: '/api/v1/simulation/create',
        method: 'POST',
        body: simulation,
      }),
      invalidatesTags: ['Simulations'],
    }),
    runSimulation: builder.mutation<any, string>({
      query: (simulation_id) => ({
        url: `/api/v1/simulation/${simulation_id}/run`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'Simulations', id }],
    }),
    getSimulationResults: builder.query<any, string>({
      query: (simulation_id) => `/api/v1/simulation/${simulation_id}/results`,
      providesTags: (result, error, id) => [{ type: 'Simulations', id }],
    }),

    // Supply Chain endpoints
    getSupplyChain: builder.query<SupplyChain, void>({
      query: () => '/api/v1/supply-chain',
      providesTags: ['SupplyChain'],
    }),
    updateSupplyChain: builder.mutation<{ status: string; message: string }, SupplyChain>({
      query: (supplyChain) => ({
        url: '/api/v1/supply-chain',
        method: 'POST',
        body: supplyChain,
      }),
      invalidatesTags: ['SupplyChain'],
    }),

    // Resilience Score endpoint
    getResilienceScore: builder.query<ResilienceScore, void>({
      query: () => '/api/v1/resilience-score',
    }),

    // Sustainability Metrics endpoint
    getSustainabilityMetrics: builder.query<SustainabilityMetrics, void>({
      query: () => '/api/v1/sustainability-metrics',
    }),

    // OSINT Global Signal Detection endpoints
    getActiveDisruptions: builder.query<any[], void>({
      query: () => '/api/v1/osint/active-disruptions',
      providesTags: ['Disruptions'],
    }),
    getOsintDisruptions: builder.query<any, { severity?: string; status?: string; region?: string }>({
      query: (params) => ({
        url: '/api/v1/osint/disruptions',
        params,
      }),
      providesTags: ['Disruptions'],
    }),
    getOsintSignals: builder.query<any, { type?: string; severity?: string; region?: string; hours?: number }>({
      query: (params) => ({
        url: '/api/v1/osint/signals',
        params,
      }),
    }),
    getOsintRiskSummary: builder.query<any, void>({
      query: () => '/api/v1/osint/risk-summary',
    }),
    getOsintDashboard: builder.query<any, void>({
      query: () => '/api/v1/osint/dashboard',
    }),
    createOsintSignal: builder.mutation<any, any>({
      query: (signal) => ({
        url: '/api/v1/osint/signals',
        method: 'POST',
        body: signal,
      }),
      invalidatesTags: ['Disruptions'],
    }),
    updateDisruptionStatus: builder.mutation<any, { id: string; status: string; notes?: string }>({
      query: ({ id, ...body }) => ({
        url: `/api/v1/osint/disruptions/${id}/status`,
        method: 'PATCH',
        body,
      }),
      invalidatesTags: ['Disruptions'],
    }),

    // Recommendations endpoint
    getRecommendations: builder.query<Recommendation[], void>({
      query: () => '/api/v1/recommendations',
    }),

    // Simulations endpoints
    getSimulations: builder.query<Simulation[], void>({
      query: () => '/api/v1/simulation/list',
      providesTags: ['Simulations'],
    }),
    getSimulation: builder.query<Simulation, string>({
      query: (id) => `/api/v1/simulations/${id}`,
      providesTags: (result, error, id) => [{ type: 'Simulations', id }],
    }),

    // Disruption Scenarios endpoints
    getDisruptionScenarios: builder.query<DisruptionScenario[], void>({
      query: () => '/api/v1/disruptions',
      providesTags: ['Disruptions'],
    }),
    createDisruptionScenario: builder.mutation<DisruptionScenario, Partial<DisruptionScenario>>({
      query: (scenario) => ({
        url: '/api/v1/disruptions',
        method: 'POST',
        body: scenario,
      }),
      invalidatesTags: ['Disruptions'],
    }),

    // Tasks endpoints
    getTasks: builder.query<Task[], void>({
      query: () => '/api/v1/tasks',
      providesTags: ['Tasks'],
    }),
    getTask: builder.query<Task, string>({
      query: (id) => `/api/v1/tasks/${id}`,
      providesTags: (result, error, id) => [{ type: 'Tasks', id }],
    }),
    createTask: builder.mutation<Task, Partial<Task>>({
      query: (task) => ({
        url: '/api/v1/tasks',
        method: 'POST',
        body: task,
      }),
      invalidatesTags: ['Tasks'],
    }),

    // Analytics endpoints
    getAnalytics: builder.query<Analytics, void>({
      query: () => '/api/v1/analytics/',
      providesTags: ['Analytics'],
    }),
    getAnalyticsOverview: builder.query<any, void>({
      query: () => '/api/v1/analytics/overview',
      providesTags: ['Analytics'],
    }),
    getSustainabilityAnalytics: builder.query<Analytics['sustainability'], void>({
      query: () => '/api/v1/analytics/sustainability',
      providesTags: ['Analytics'],
    }),
    getResilienceAnalytics: builder.query<Analytics['resilience'], void>({
      query: () => '/api/v1/analytics/resilience',
      providesTags: ['Analytics'],
    }),
  }),
});

// Export hooks for usage in components
export const {
  // Auth hooks
  useLoginMutation,
  useRegisterMutation,
  
  // Agent hooks
  useProcessTaskMutation,
  useGetTaskHistoryQuery,
  useClearTaskHistoryMutation,
  useGetAgentCapabilitiesQuery,
  useGetAgentStatusQuery,
  useGetTaskQueueQuery,
  useSearchMemoryQuery,
  useGetMemoryStatsQuery,

  // Subscription hooks
  useGetSubscriptionPlansQuery,
  useGetCurrentSubscriptionQuery,
  useUpdateSubscriptionMutation,

  // Usage hooks
  useGetUsageStatisticsQuery,
  useGetFeatureUsageQuery,

  // Feature flag hooks
  useGetFeatureFlagsQuery,
  useUpdateFeatureFlagMutation,

  // Simulation hooks
  useCreateSimulationMutation,
  useRunSimulationMutation,
  useGetSimulationResultsQuery,

  // Existing hooks
  useGetSupplyChainQuery,
  useUpdateSupplyChainMutation,
  useGetResilienceScoreQuery,
  useGetSustainabilityMetricsQuery,
  useGetActiveDisruptionsQuery,
  useGetRecommendationsQuery,
  useGetSimulationsQuery,
  useGetSimulationQuery,
  useGetDisruptionScenariosQuery,
  useCreateDisruptionScenarioMutation,
  useGetTasksQuery,
  useGetTaskQuery,
  useCreateTaskMutation,
  useGetAnalyticsQuery,
  useGetAnalyticsOverviewQuery,
  useGetSustainabilityAnalyticsQuery,
  useGetResilienceAnalyticsQuery,

  // OSINT Global Signal Detection hooks
  useGetOsintDisruptionsQuery,
  useGetOsintSignalsQuery,
  useGetOsintRiskSummaryQuery,
  useGetOsintDashboardQuery,
  useCreateOsintSignalMutation,
  useUpdateDisruptionStatusMutation,
} = api;
